import React, { useCallback, useMemo, useRef } from 'react';

import { Loading } from '@/components/shared/Loading/Loading';
import { NoResults } from '@/components/shared/NoResults/NoResults';
import { hubApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useContentViewStore } from '@/lib/store/contentViewStore';
import { ContentStatus, ExploreBlock } from '@addmile/backend-client';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import { contentKeys } from '../../services/ContentManagerService';
import BlockCard from './BlockCard';
import BlockFilters, { GetBlockParams } from './BlockFilters';
import BlockPagination from './BlockPagination';

type BlocksPage = {
  blocks: ExploreBlock[];
  hasMore: boolean;
  nextCursor?: number;
};

interface BlockListProps {
  onEditBlock: (slug: string) => void;
  onDeleteBlock: (slug: string) => void;
  onCopyBlock: (slug: string) => void;
}

export const BlockList: React.FC<BlockListProps> = ({
  onEditBlock,
  onDeleteBlock,
  onCopyBlock,
}) => {
  const filters = useContentViewStore((state) => state.blockFilters);
  const setFilters = useContentViewStore((state) => state.setBlockFilters);
  const page = useContentViewStore((state) => state.blockPage);
  const setPage = useContentViewStore((state) => state.setBlockPage);

  const listTopRef = useRef<HTMLDivElement>(null);
  const showPage = (index: number) => {
    setPage(index);
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const debouncedTitle = useDebounce(filters.title, 300);

  const queryFilters = useMemo(
    () => ({
      ...filters,
      title: debouncedTitle,
    }),
    [filters, debouncedTitle],
  );

  const fetchBlocks = useCallback(
    async ({ pageParam = 0 }: { pageParam?: number }) => {
      const token = await getAuthToken();
      const params: GetBlockParams = {
        ...queryFilters,
        cursor: pageParam,
        pageSize: 24,
      };

      const { data } = await hubApi.getExploreBlocks(
        params.title,
        params.type,
        params.status as ContentStatus[],
        params.language,
        params.cursor,
        params.pageSize,
        withAuthHeader(token),
      );

      const lightBlocks: ExploreBlock[] = data.blocks;

      const maxId = lightBlocks.reduce((max, block) => Math.max(max, block.id), pageParam);
      const nextCursor = data.hasMore ? maxId : undefined;

      return {
        blocks: lightBlocks,
        hasMore: data.hasMore,
        nextCursor,
      };
    },
    [queryFilters],
  );

  const { data, isLoading, isError, fetchNextPage, hasNextPage } = useInfiniteQuery<
    BlocksPage,
    Error,
    InfiniteData<BlocksPage>,
    ReturnType<typeof contentKeys.blocksList>,
    number
  >({
    queryKey: contentKeys.blocksList(queryFilters),
    queryFn: ({ pageParam = 0 }) => fetchBlocks({ pageParam }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    initialPageParam: 0,
    refetchOnWindowFocus: false,
  });

  const pages = data?.pages ?? [];
  // The remembered page outlives the cached pages, so a cold load can hold a page number that was
  // never fetched. Clamping lands on the newest loaded page instead of rendering an empty list.
  const currentPage = Math.min(page, Math.max(pages.length - 1, 0));
  const currentPageBlocks: ExploreBlock[] = pages[currentPage]?.blocks ?? [];
  const canGoNextPage = currentPage < pages.length - 1 || !!hasNextPage;

  const handleFilterChange = (
    key: keyof GetBlockParams,
    value: GetBlockParams[keyof GetBlockParams],
  ) => {
    setFilters({ ...filters, [key]: value });
    setPage(0);
  };

  const handleNextPage = async () => {
    const nextIndex = currentPage + 1;
    // If we already have that page loaded, just move the pointer
    if (pages[nextIndex]) {
      showPage(nextIndex);
      return;
    }
    if (hasNextPage) {
      await fetchNextPage();
      showPage(nextIndex);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      showPage(currentPage - 1);
    }
  };

  const content = useMemo(() => {
    if (isLoading) {
      return <Loading />;
    }
    if (isError || !currentPageBlocks.length) {
      return <NoResults />;
    }
    return currentPageBlocks.map((block) => (
      <BlockCard
        key={block.slug}
        block={block}
        onEditBlock={onEditBlock}
        onDeleteBlock={onDeleteBlock}
        onCopyBlock={onCopyBlock}
      />
    ));
  }, [isLoading, isError, currentPageBlocks, onEditBlock, onDeleteBlock, onCopyBlock]);

  return (
    <div ref={listTopRef} className="grid gap-4">
      <BlockFilters filters={filters} onFilterChange={handleFilterChange} />

      <div className="flex flex-col gap-4">{content}</div>

      {(currentPage > 0 || canGoNextPage) && (
        <BlockPagination
          currentPage={currentPage}
          hasMore={canGoNextPage}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
        />
      )}
    </div>
  );
};

export default BlockList;
